import classcat from "classcat";
import React from "react";

import { IconZoomIn, Spinner } from "@touchtech/web-baselayer-ui";

import QRCodeComponent from "@is/components/QRCodeComponent";
import { QR_CODE_SIZES } from "@is/constants";
import { CheckoutConfiguration } from "@is/types";
import { convertCurrencyCodeToSymbol } from "@is/utils";

import BasketItem from "./BasketItem";
import css from "./BasketItems.module.css";
import useManageBasketItems from "./hooks/useManageBasketItems";

type Props = {
  isCheckout?: boolean;
  isSummary?: boolean;
  setItemPending?: React.Dispatch<React.SetStateAction<boolean>>;
  enabledQRCodeCheckoutConfiguration?: CheckoutConfiguration;
};

const BasketItems = ({
  isCheckout,
  setItemPending,
  isSummary,
  enabledQRCodeCheckoutConfiguration,
}: Props) => {
  const {
    totalPrice,
    totalItems,
    currencyCode,
    basketItems,
    handleChangeItemQuantity,
    handleRemoveItem,
    handleClickItem,
    handleOpenQRCodeModal,
    qrCodeUrl,
    qrCheckoutInProgress,
    t,
  } = useManageBasketItems(setItemPending, enabledQRCodeCheckoutConfiguration);

  if (!basketItems.length) {
    return (
      <div
        className={classcat(["text-headline-5xl", css.emptyBasketPlaceholder])}
      >
        {t("Your basket is empty!")}
      </div>
    );
  }

  return (
    <div
      className={classcat([
        css.container,
        {
          [css.summaryContainer]: isSummary,
          [css.checkoutContainer]: isCheckout,
        },
      ])}
    >
      {isSummary && (
        <div className={classcat(["text-label-2xl", css.totalItems])}>
          <div>
            {t("Total")}{" "}
            {`${totalItems} ${totalItems > 1 ? t("Items") : t("Item")}`}
          </div>
        </div>
      )}
      <div
        className={classcat([
          css.itemsContainer,
          { [css.checkout]: isCheckout },
        ])}
      >
        {basketItems.map((item) => (
          <BasketItem
            key={item.ean}
            item={item}
            isCheckout={isCheckout}
            onChangeQuantity={handleChangeItemQuantity}
            onRemove={handleRemoveItem}
            onClick={handleClickItem}
          />
        ))}
      </div>
      {!isCheckout && (
        <div className={classcat(["text-label-2xl", css.totalItems])}>
          <div className={css.priceContainer}>
            <div>
              {t("Total")}{" "}
              {`${totalItems} ${totalItems > 1 ? t("Items") : t("Item")}`}
            </div>
            <div>
              {!!totalPrice && (
                <div className={css.price}>
                  <strong>
                    {`${totalPrice} ${convertCurrencyCodeToSymbol(
                      currencyCode
                    )}`}
                  </strong>
                </div>
              )}
            </div>
          </div>
          {!!enabledQRCodeCheckoutConfiguration && !qrCheckoutInProgress && (
            <div
              className={css.qrCodeContainer}
              onClick={handleOpenQRCodeModal}
            >
              <div className={css.qrCode}>
                <QRCodeComponent value={qrCodeUrl} size={QR_CODE_SIZES.Small} />
              </div>
              <div className={css.qrCodeTextContainer}>
                <span className={classcat([css.scanText, "text-label-xl"])}>
                  {t("Scan here for quick checkout")}
                </span>
                <div className={css.tapToEnlargeContainer}>
                  <div className={css.zoomInIcon}>
                    <IconZoomIn className="text-label-lg" />
                  </div>

                  <span
                    className={classcat([css.tapToEnlarge, "text-label-base"])}
                  >
                    {t("Tap to enlarge")}
                  </span>
                </div>
              </div>
            </div>
          )}
          {!!enabledQRCodeCheckoutConfiguration && qrCheckoutInProgress && (
            <div className={classcat([css.qrCodeContainer, css.pending])}>
              <div className={css.loader}>
                <Spinner />
              </div>
              <div className={classcat(["text-label-xl", css.pendingText])}>
                {t("Please wait while creating your basket")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BasketItems;
